import React, { useState, useEffect } from "react";

import {
  Autocomplete,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";

import { SolutionsFrame } from "components/solutions/SolutionsFrame";
import { BlockStandard } from "components/admin/BlockStandard";
import {
  CityResponse,
  useGreenEnergyContext,
} from "contexts/GreenEnergyContext";
import { fetchCitiesList, fetchCityOffers } from "api/onboarding";
import { EnergyGroups } from "api/onboardingTypes";
import { useToken } from "contexts/UserContext";
import { useSetError, useErrorInfo } from "contexts/ErrorContext";
import { CustomCodes } from "contexts/ErrorContextData";

const FRAME_DATA = {
  imgSrc: "/static/img/solutions/map.png",
  imgAlt: "Um painel solar com uma avaliação positiva",
  title: "Contrato de energia verde",
  buttonText: "Continuar",
  currentStep: 1,
  totalSteps: 6,
};

const INVOICE_UPLOAD_PAGE = "/onboarding/consumer-bill-upload";
const OFFER_SELECTION_PAGE = "/onboarding/green-energy-offers";

const GROUP_DATA: Array<{ value: EnergyGroups; text: string }> = [
  {
    value: "groupB",
    text: "Baixa tensão (Grupo B)",
  },
  {
    value: "groupA",
    text: "Média/Alta tensão (Grupo A)",
  },
  {
    value: "idk",
    text: "Não sei",
  },
];

export const BuildingBasics = ({ history }) => {
  const token = useToken();
  const [chosenCity, setChosenCity] = useState<CityResponse[0] | null>();
  const [chosenGroup, setChosenGroup] = useState<EnergyGroups>();
  const { setEnergyGroup, setCity } = useGreenEnergyContext();
  const [isLoading, setIsLoading] = useState<"cities" | "offers" | false>(
    false
  );
  const [cities, setCities] = useState<CityResponse>([]);
  const setGlobalError = useSetError();
  const errorInfo = useErrorInfo();
  const hasFormError = errorInfo?.code === CustomCodes.INCOMPLETE_FORM;

  useEffect(() => {
    const getCities = async () => {
      setIsLoading("cities");
      try {
        const data = await fetchCitiesList(token);
        setCities(data);
      } catch (error) {
        // @ts-expect-error
        setGlobalError(error.response?.data?.error);
      } finally {
        setIsLoading(false);
      }
    };
    getCities();
  }, [token, setGlobalError]);

  const handleClick = () => {
    if (chosenCity && chosenGroup) {
      // pass selections into the Context
      setEnergyGroup(chosenGroup);
      setCity(chosenCity);
      if (chosenGroup !== "groupB") {
        history.push(INVOICE_UPLOAD_PAGE);
      } else {
        findContracts(`${chosenCity.city} - ${chosenCity.sigla}`);
      }
    } else {
      setGlobalError(CustomCodes.INCOMPLETE_FORM);
    }
  };

  const findContracts = async (city) => {
    setIsLoading("offers");
    try {
      const offers = await fetchCityOffers(token, city);
      history.push(OFFER_SELECTION_PAGE, { offers });
    } catch (error) {
      history.push(INVOICE_UPLOAD_PAGE);
    }
  };

  return (
    <SolutionsFrame
      {...FRAME_DATA}
      onButtonClick={handleClick}
      isLoading={!!isLoading}
    >
      <BlockStandard hideDivider>
        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
          Indique a cidade do imóvel que busca solução para verificarmos as
          melhores ofertas pra você.
        </Typography>
      </BlockStandard>

      <BlockStandard hideDivider>
        <Autocomplete
          clearOnBlur
          value={chosenCity}
          getOptionLabel={({ city, sigla }) => `${city} - ${sigla}`}
          onChange={(e, newValue) => setChosenCity(newValue)}
          disabled={!cities?.length}
          loading={isLoading === "cities"}
          loadingText="Carregando a lista de cidades..."
          options={cities}
          renderInput={(params) => (
            <TextField
              {...params}
              error={hasFormError && !chosenCity}
              variant="outlined"
            />
          )}
        />
      </BlockStandard>

      <BlockStandard hideDivider>
        <Typography variant="body2">
          Está em baixa, média ou alta tensão?
        </Typography>
        <List>
          {GROUP_DATA.map(({ value, text }) => (
            <ListItem
              disablePadding
              key={value}
              sx={{
                border: `1px solid ${
                  hasFormError && !chosenGroup ? "red" : "#595959"
                }`,
                borderRadius: "12px",
                mt: 1,
              }}
            >
              <ListItemButton onClick={() => setChosenGroup(value)}>
                <ListItemIcon>
                  <Checkbox
                    sx={{ color: "#595959" }}
                    color="success"
                    edge="start"
                    checked={chosenGroup === value}
                  />
                </ListItemIcon>
                <ListItemText primary={text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </BlockStandard>
    </SolutionsFrame>
  );
};
